import * as React from 'react';
import { AppShell } from '@/components/app-shell';
import { InvitePage } from '@/components/auth/invite-page';
import { LoginGate } from '@/components/auth/login-gate';
import { OnboardingWizard } from '@/components/auth/onboarding-wizard';
import { api } from '@/lib/api';
import { setInstanceBootstrapped } from '@/lib/bootstrapped';
import './index.css';

/**
 * App boot gate (REQ-062 Parça A, REQ-063 + REQ-066 onboarding, REQ-076
 * fail-closed): fresh instances (unbootstrapped, onboarding not done) boot
 * into the full-screen onboarding wizard; bootstrapped AND `requireLogin`
 * on hides the shell behind login until `/api/auth/me` 200s. Gate-off (or
 * fresh-but-onboarded-open) instances boot straight into the shell —
 * legacy behavior, untouched. When the settings check itself fails
 * (server down / network error) the shell NEVER opens — the login screen
 * shows instead, so a backend outage cannot silently expose the harness.
 */

type GateState = { phase: 'loading' } | { phase: 'open' } | { phase: 'onboarding' } | { phase: 'gated'; mode: 'login' | 'register' };

function useSessionId(): string {
  const [id, setId] = React.useState('sess_phase0_demo');
  React.useEffect(() => {
    const key = 'lokma:sessionId';
    const existing = localStorage.getItem(key);
    if (existing) {
      setId(existing);
      return;
    }
    const fresh = `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    localStorage.setItem(key, fresh);
    setId(fresh);
  }, []);
  return id;
}

function useGate(): { gate: GateState; refresh: () => void } {
  const [gate, setGate] = React.useState<GateState>({ phase: 'loading' });
  const check = React.useCallback(() => {
    setGate({ phase: 'loading' });
    void (async () => {
      try {
        const settingsRes = await api.getAuthSettings();
        // REQ-169 — seed the shell-wide bootstrapped flag from this SAME
        // fetch (no extra request): once the first admin exists, the Setup
        // rail entry hides. This runs again on every reload, so the state
        // is re-verified on each boot.
        setInstanceBootstrapped(settingsRes.bootstrapped);
        if (!settingsRes.bootstrapped) {
          // Fresh instance (REQ-066): first launch shows the onboarding
          // wizard; a fresh-but-onboarded-open instance (owner picked
          // "no login") boots straight into the shell.
          setGate(settingsRes.settings.onboardingDone ? { phase: 'open' } : { phase: 'onboarding' });
          return;
        }
        if (!settingsRes.settings.requireLogin) {
          setGate({ phase: 'open' });
          return;
        }
        try {
          await api.authMeQuiet();
          setGate({ phase: 'open' });
        } catch {
          setGate({ phase: 'gated', mode: 'login' });
        }
      } catch {
        // Settings unreadable (server down?) — fail CLOSED (REQ-076): show
        // the login screen, never the shell. A failed login surfaces the
        // outage honestly instead of exposing the harness.
        setGate({ phase: 'gated', mode: 'login' });
      }
    })();
  }, []);
  React.useEffect(() => {
    check();
  }, [check]);
  return { gate, refresh: check };
}

export default function App() {
  const sessionId = useSessionId();
  const { gate, refresh } = useGate();
  // REQ-080: invite links land here BEFORE the gate — an invited user has
  // no password yet, so the login gate would 401 them forever.
  const inviteToken = React.useMemo(() => {
    try {
      if (window.location.pathname !== '/invite') return null;
      return new URLSearchParams(window.location.search).get('token') ?? '';
    } catch {
      return null;
    }
  }, []);
  if (inviteToken !== null) {
    return <InvitePage token={inviteToken} onDone={refresh} />;
  }
  if (gate.phase === 'loading') {
    return <div className="h-screen w-screen grid place-items-center bg-[#FAF9F5] dark:bg-[#161618] text-sm text-zinc-400">Loading Lokma…</div>;
  }
  if (gate.phase === 'gated') {
    return <LoginGate mode={gate.mode} onDone={refresh} />;
  }
  if (gate.phase === 'onboarding') {
    return <OnboardingWizard onDone={refresh} />;
  }
  return <AppShell sessionId={sessionId} />;
}
